import { createElement } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { useConfirm } from '~/components/confirm-dialog';
import { publishOrConfirm } from '~/lib/held-publish';
import type { AdminApp } from '~/features/apps/edit-app';
import { PublishSummaryBody, fetchPublishSummary, publishWithSummary, summaryDialog, type PublishVersion } from '~/features/apps/publish-summary';

/** `publishId`: the publish this made, named only for the owner of an app that keeps Git history (FRONT-3368). */
export type Published = { app: AdminApp | null; publishId?: number };

type ConfirmFn = ReturnType<typeof useConfirm>;
export type PublishSummaryOption = { goingLive: boolean; previewOn: boolean };

/**
 * The publish itself, outside React for callers that cannot hold the hook (a
 * toast's action). With `summary` (FRONT-3369) the owner first sees what the
 * publish includes and confirms it, and only that version is published. A
 * publish held for its findings (FRONT-2944) asks the owner, and "Publish
 * anyway" sends it again. Null when the owner declined either.
 */
export function publishApp(slug: string, confirmDialog: ConfirmFn, summary?: PublishSummaryOption): Promise<Published | null> {
  const post = (expected: PublishVersion | undefined) =>
    publishOrConfirm(
      (token) => {
        const body = { ...(token ? { confirm: token } : {}), ...(expected ? { expected } : {}) };
        return api.post<Published>(`/api/admin/apps/${slug}/publish`, Object.keys(body).length ? body : undefined);
      },
      confirmDialog,
    );
  if (!summary) return post(undefined);
  return publishWithSummary({
    summary: () => fetchPublishSummary(slug),
    ask: (read, moved) =>
      confirmDialog({
        ...summaryDialog(summary.goingLive),
        description: createElement(PublishSummaryBody, { slug, summary: read, moved, previewOn: summary.previewOn }),
      }),
    publish: post,
  });
}

/** The app and the lists a publish changes, read again. */
export function refreshAfterPublish(qc: QueryClient, slug: string): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['admin-apps'] }),
    qc.invalidateQueries({ queryKey: ['admin-app', slug] }),
    qc.invalidateQueries({ queryKey: ['app-published-card'] }),
    qc.invalidateQueries({ queryKey: ['resource-counts'] }),
  ]);
}

/**
 * The owner publishes the app: makes it live, or makes its latest changes live
 * when it already is, through `publishApp`. The app and the lists are read
 * again on success; each caller says what happened in its own words.
 * Options-level callbacks, so they still run when the control that started
 * the publish has unmounted.
 */
export function usePublishApp(
  slug: string,
  says: { onSuccess: (data: Published) => void; onError: (e: Error) => void },
  summary?: PublishSummaryOption,
) {
  const qc = useQueryClient();
  const confirmDialog = useConfirm();
  return useMutation({
    mutationKey: ['app-publish', slug],
    mutationFn: () => publishApp(slug, confirmDialog, summary),
    // Said first; the page reads the app again before the control stops showing it is busy.
    onSuccess: async (data) => {
      if (!data) return;
      says.onSuccess(data);
      await refreshAfterPublish(qc, slug);
    },
    onError: says.onError,
  });
}
